// Examiner beneficiary table: search and sort (#63). The table used to match
// only the English name and email, so Arabic names and phones never matched,
// and it had no sorting.
import { normalizeForSearch } from '@/lib/customers/customerDirectory';

export interface Examinee {
  firstNameEn?: string;
  lastNameEn?: string;
  firstNameAr?: string;
  lastNameAr?: string;
  phone?: string;
  email?: string;
}

export type ExamineeSortKey = 'name' | 'phone';
export type SortDirection = 'asc' | 'desc';
export interface ExamineeSort {
  key: ExamineeSortKey;
  direction: SortDirection;
}

/**
 * Saudi mobile digits without country code or trunk zero, so «0501234567»,
 * «+966 50 123 4567» and «501234567» all become «501234567».
 */
export function phoneDigits(value: string): string {
  let digits = (value || '').replace(/\D/g, '');
  if (digits.startsWith('00966')) digits = digits.slice(5);
  else if (digits.startsWith('966')) digits = digits.slice(3);
  return digits.replace(/^0+/, '');
}

const arabicName = (e: Examinee) => `${e.firstNameAr || ''} ${e.lastNameAr || ''}`.trim();
const englishName = (e: Examinee) => `${e.firstNameEn || ''} ${e.lastNameEn || ''}`.trim();

/** Arabic name, English name, email, or phone digits (spaces, +966 and leading 0 ignored). */
export function matchesExamineeSearch(e: Examinee, query: string): boolean {
  const term = normalizeForSearch(query || '');
  if (!term) return true;
  const texts = [arabicName(e), englishName(e), e.email || ''].map(normalizeForSearch);
  if (texts.some((t) => t.includes(term))) return true;

  // Only digit-like queries are phone searches («05», «+966 5», «50 12»).
  if (!/^[\d\s+()-]+$/.test(query.trim())) return false;
  const q = phoneDigits(query);
  return q.length > 0 && phoneDigits(e.phone || '').includes(q);
}

/** The name the table shows: Arabic in RTL (falling back to English), English otherwise. */
export function examineeDisplayName(e: Examinee, rtl: boolean): string {
  return rtl ? arabicName(e) || englishName(e) : englishName(e) || arabicName(e);
}

/**
 * Sorted copy. Rows missing the sort value (no name / no phone) go last in
 * both directions so they never crowd the top of the list.
 */
export function sortExaminees<T extends Examinee>(rows: T[], sort: ExamineeSort | null, rtl: boolean): T[] {
  if (!sort) return rows;
  const valueOf = (e: T) =>
    sort.key === 'name' ? normalizeForSearch(examineeDisplayName(e, rtl)) : phoneDigits(e.phone || '');
  const sign = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (!va && !vb) return 0;
    if (!va) return 1;
    if (!vb) return -1;
    const cmp = sort.key === 'name'
      ? va.localeCompare(vb, rtl ? 'ar' : 'en')
      : va.localeCompare(vb, 'en', { numeric: true });
    return sign * cmp;
  });
}

/** Header click: a new column sorts ascending, the same column flips, a third click clears. */
export function nextExamineeSort(current: ExamineeSort | null, key: ExamineeSortKey): ExamineeSort | null {
  if (!current || current.key !== key) return { key, direction: 'asc' };
  if (current.direction === 'asc') return { key, direction: 'desc' };
  return null;
}
